import { DeleteOutlined, EditOutlined, PlusCircleOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@apollo/client/react';
import { filterByTokens } from '@medcom/shared-ui/utils/tokenSearch.ts';
import { App, Button, Form, Input, Modal, Popconfirm, Select, Space, Table, Tag } from 'antd';
import type React from 'react';
import { useMemo, useState } from 'react';
import type { TagInput } from '../../../graphql/generated/graphql.ts';
import { CREATE_TAG_MUTATION, DELETE_TAG_MUTATION, TAGS_QUERY, UPDATE_TAG_MUTATION } from './TagManagementModal.gql';
import { compareByTag, TAG_COLORS } from './tagColors.ts';

export const TagManagementModal: React.FC<{
  open: boolean;
  onClose: () => void;
  onChange?: () => void;
}> = ({ open, onClose, onChange }) => {
  const { message } = App.useApp();
  const [form] = Form.useForm<TagInput>();
  const [editingId, setEditingId] = useState<string | null>(null);

  const { data, loading, refetch } = useQuery(TAGS_QUERY, { skip: !open });
  const [createTag, { loading: creating }] = useMutation(CREATE_TAG_MUTATION);
  const [updateTag, { loading: updating }] = useMutation(UPDATE_TAG_MUTATION);
  const [deleteTag] = useMutation(DELETE_TAG_MUTATION);

  const tags = data?.tags ?? [];
  const [search, setSearch] = useState('');
  const filteredTags = useMemo(() => [...filterByTokens(tags, search, [t => t.name])].sort(compareByTag), [tags, search]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      if (editingId) {
        await updateTag({ variables: { id: editingId, input: values } });
        message.success('Tag aktualisiert');
      } else {
        await createTag({ variables: { input: values } });
        message.success('Tag erstellt');
      }
      form.resetFields();
      setEditingId(null);
      refetch();
      onChange?.();
    } catch (e) {
      if (e instanceof Error) message.error(e.message);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteTag({ variables: { id } });
      message.success('Tag gelöscht');
      refetch();
      onChange?.();
    } catch (e) {
      if (e instanceof Error) message.error(e.message);
    }
  };

  const handleEdit = (record: { id: string; name: string; color: string }) => {
    setEditingId(record.id);
    form.setFieldsValue({ name: record.name, color: record.color });
  };

  const handleCancel = () => {
    setEditingId(null);
    form.resetFields();
  };

  return (
    <Modal title="Tags verwalten" open={open} onCancel={onClose} footer={null} width={600} destroyOnHidden>
      <Form form={form} layout="inline" onFinish={handleSave} style={{ marginBottom: 16 }}>
        <Form.Item name="name" rules={[{ required: true, message: 'Name erforderlich' }]} style={{ flex: 1 }}>
          <Input placeholder="Tag-Name" />
        </Form.Item>
        <Form.Item name="color" rules={[{ required: true, message: 'Farbe wählen' }]}>
          <Select placeholder="Farbe" style={{ width: 150 }} popupMatchSelectWidth={false}>
            {TAG_COLORS.map(c => (
              <Select.Option key={c} value={c}>
                <Tag color={c}>{c}</Tag>
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item style={{ marginRight: 0 }}>
          <Space>
            <Button
              type="primary"
              htmlType="submit"
              icon={editingId ? <EditOutlined /> : <PlusCircleOutlined />}
              loading={creating || updating}
            >
              {editingId ? 'Speichern' : 'Erstellen'}
            </Button>
            {editingId && <Button onClick={handleCancel}>Abbrechen</Button>}
          </Space>
        </Form.Item>
      </Form>
      <Input
        allowClear
        placeholder="Tags durchsuchen..."
        value={search}
        onChange={e => setSearch(e.target.value)}
        style={{ marginBottom: 8 }}
      />
      <Table
        rowKey="id"
        size="small"
        loading={loading}
        dataSource={filteredTags}
        pagination={false}
        scroll={{ y: 300 }}
        columns={[
          {
            title: 'Name',
            dataIndex: 'name',
            render: (name, record) => <Tag color={record.color}>{name}</Tag>,
          },
          {
            title: 'Aktionen',
            width: 100,
            render: (_, record) => (
              <Space>
                <Button type="text" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
                <Popconfirm
                  title="Tag löschen?"
                  description="Der Tag wird bei allen Zuweisern entfernt."
                  onConfirm={() => handleDelete(record.id)}
                >
                  <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                </Popconfirm>
              </Space>
            ),
          },
        ]}
      />
    </Modal>
  );
};
